import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, List, ListItem, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { compact, isEmpty, isNil, map, reduce } from 'lodash';
import useRoles from 'common/hooks/useRoles';
import lineItemStatusEnum from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import LoadingModule from 'common/components/LoadingModule';
import { listPatientOrders, listPatientPrescriptions } from 'store/thunks/patientThunks';
import { PrintLabelButton, PrintLabelProvider } from 'common/hooks/usePrintLabelContext';
import useTabContext from 'common/hooks/useTabContext';
import useLineItemUpdates from 'common/hooks/useLineItemUpdates';
import TransferRequestAccordion from './TransferRequestAccordion';
import PatientOrdersAccordion from './PatientOrdersAccordion';

const ActionItemEligibility = () => {
  const dispatch = useDispatch();
  const { mpi } = useParams();
  const requests = useSelector(({ patient }) => patient[mpi]?.requests);
  const orders = useSelector(({ patient }) => patient[mpi]?.orders);
  const hasAccess = useRoles();
  const isActiveTab = useTabContext()?.tab === 'PatientActionItemsTab';

  useLineItemUpdates(mpi, isActiveTab);

  useMemo(() => {
    if (isActiveTab) {
      dispatch(listPatientOrders({ mpi }));
    }
  }, [dispatch, isActiveTab, mpi]);

  useEffect(() => {
    dispatch(listPatientPrescriptions({ mpi }));
  }, [dispatch, mpi]);

  if (isNil(orders)) {
    return <LoadingModule />;
  }

  return (
    <>
      {!isEmpty(requests) && (
        <Typography variant='h6' component='h2' sx={{ p: 1 }}>
          Requests
        </Typography>
      )}

      <List>
        {map(requests, (request) => (
          <ListItem key={request.requestId}>
            <TransferRequestAccordion
              mpi={mpi}
              requestCreatedAt={request.createdAt}
              requestId={request.requestId}
              transferRequest={request.transferRequest}
            />
          </ListItem>
        ))}

        <PrintLabelProvider
          initialValue={reduce(
            orders,
            (result, order) => [
              ...result,
              ...(!order.isProcessedExternal
                ? [
                    ...compact(
                      map(order.lineItems, (lineItem) =>
                        !lineItem?.isPrinted &&
                        lineItem.lineItemStatus !== lineItemStatusEnum.RE_PROCESS &&
                        lineItem.lineItemStatus !== lineItemStatusEnum.CANCELED &&
                        lineItem.processingState !== lineItemStatusEnum.CANCELED &&
                        lineItem.processingState !== 'FILL_LATER' &&
                        order.status !== orderEnum.CANCELED &&
                        order.status !== orderEnum.FILL_LATER &&
                        !lineItem.taggedForReview &&
                        lineItem?.isLabelCreated
                          ? lineItem?.lineItemId
                          : null
                      )
                    ),
                  ]
                : []),
            ],
            []
          )}
        >
          {!isEmpty(orders) && (
            <Grid container direction='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='h6' component='h2' sx={{ p: 1 }}>
                Orders
              </Typography>
              {hasAccess.generateBulkLabels && <PrintLabelButton />}
            </Grid>
          )}
          <List>
            {map(orders, (order) => (
              <ListItem key={order.orderId}>
                <PatientOrdersAccordion order={order} mpi={mpi} />
              </ListItem>
            ))}
          </List>
        </PrintLabelProvider>
      </List>
    </>
  );
};

export default ActionItemEligibility;
